import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { Button, FormFeedback } from "reactstrap";

import Icon from "shared/react_components/MaterialIcon";

import { editor, field, mirror } from "./InlineEditor.module.scss";

const InlineEditor = ({ label, name, children, onSubmit, ...props }) => {
  const [isEditing, setEditing] = useState(false);

  const submitAndClose = useCallback(
    async (values, actions) => {
      if (await onSubmit(values, actions)) {
        setEditing(false);
      } else {
        actions.setSubmitting(false);
      }
    },
    [onSubmit]
  );

  return (
    <Formik enableReinitialize onSubmit={submitAndClose} {...props}>
      {({ values, isValid, isSubmitting, resetForm }) => (
        <Form
          noValidate
          className="d-inline-flex align-items-center position-relative"
        >
          <div className={editor}>
            <Field
              aria-label={label}
              name={name}
              className={field}
              readOnly={!isEditing}
              onMouseDown={() => setEditing(true)}
            />
            <span className={mirror}>{values[name]}</span>
          </div>
          <ErrorMessage name={name}>
            {msg => (
              <FormFeedback tooltip className="d-block">
                {msg}
              </FormFeedback>
            )}
          </ErrorMessage>

          {isEditing ? (
            <React.Fragment>
              <Button
                type="submit"
                color="primary"
                className="p-1 ml-2"
                disabled={isSubmitting || !isValid}
              >
                <Icon name="check" />
              </Button>
              <Button
                type="button"
                color="secondary"
                className="p-1 mx-2"
                onClick={() => {
                  setEditing(false);
                  resetForm();
                }}
              >
                <Icon name="close" />
              </Button>
            </React.Fragment>
          ) : (
            children
          )}
        </Form>
      )}
    </Formik>
  );
};

InlineEditor.propTypes = {
  /**
   * An accessibility label for the field.
   */
  label: PropTypes.string.isRequired,
  /**
   * A name for the field. Used to lookup the value in `initialValues`.
   */
  name: PropTypes.string.isRequired,
  /**
   * Any children to toggle while in display/edit mode.
   */
  children: PropTypes.node,
  /**
   * Function to run on submitting the form.  Must return if the operation was successful.
   * onSubmit(values: Values, formikBag: formikBag) => boolean | Promise<boolean>
   */
  onSubmit: PropTypes.func
};

InlineEditor.defaultProps = {
  children: false,
  onSubmit: () => true
};

export default InlineEditor;
